import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { apiDelete, apiGet, apiPost, formatEuro } from "../../lib/api";
import { sortPizzeriaMenuItems } from "../../lib/pizzeria-menu-schema";
import { buildAdminAuthHeaders, isUnauthorizedAdminError, shortMenuItemId } from "./admin-utils";
import {
  buildDraftForSection,
  normalizePizzeriaMenuApiItem,
  SECTION_OPTIONS,
  toPizzeriaMenuPayload
} from "./pizzeria-menu-admin-utils";
import "../Shop.css";

function getSectionLabel(sectionKey) {
  return SECTION_OPTIONS.find((option) => option.value === sectionKey)?.label || sectionKey;
}

function getSubsectionLabel(subsectionKey) {
  if (subsectionKey === "PIZZE_CLASSICHE") {
    return "Pizze classiche";
  }
  if (subsectionKey === "PIZZE_BIANCHE") {
    return "Pizze bianche";
  }
  return "";
}

const AdminMenuPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [newItemDraft, setNewItemDraft] = useState(() => buildDraftForSection("PIZZE"));
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState("");
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const loadItems = useCallback(
    async ({ refreshOnly = false } = {}) => {
      if (!adminToken) {
        return;
      }

      setError("");
      if (refreshOnly) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const payload = await apiGet("/api/admin/pizzeria-menu", {
          headers: buildAdminAuthHeaders(adminToken)
        });

        const normalizedItems = Array.isArray(payload?.items)
          ? payload.items.map(normalizePizzeriaMenuApiItem).filter(Boolean)
          : [];

        setItems(sortPizzeriaMenuItems(normalizedItems));
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : "Errore caricamento menu pizzeria";
        if (isUnauthorizedAdminError(loadError)) {
          handleUnauthorized();
          return;
        }
        setError(message);
      } finally {
        if (refreshOnly) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [adminToken, handleUnauthorized]
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) {
      return;
    }

    setSaveMessage(String(notice));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleNewDraftChange = (field, value) => {
    if (field === "section_key") {
      const nextDraft = buildDraftForSection(value);
      setNewItemDraft((current) => ({
        ...nextDraft,
        name: current.name,
        description: current.description,
        sort_order: current.sort_order,
        active: current.active
      }));
      return;
    }

    setNewItemDraft((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleCreateItem = async (event) => {
    event.preventDefault();
    setIsCreating(true);
    setError("");
    setSaveMessage("");

    try {
      await apiPost("/api/admin/pizzeria-menu", toPizzeriaMenuPayload(newItemDraft), {
        headers: buildAdminAuthHeaders(adminToken)
      });
      setSaveMessage("Voce menu creata.");
      setNewItemDraft(buildDraftForSection(newItemDraft.section_key));
      await loadItems({ refreshOnly: true });
    } catch (createError) {
      const message = createError instanceof Error ? createError.message : "Errore creazione voce menu";
      if (isUnauthorizedAdminError(createError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleItemDelete = async (itemId) => {
    const confirmed = window.confirm("Eliminare questa voce menu?");
    if (!confirmed) {
      return;
    }

    setDeletingItemId(itemId);
    setError("");
    setSaveMessage("");

    try {
      await apiDelete(`/api/admin/pizzeria-menu/${itemId}`, {
        headers: buildAdminAuthHeaders(adminToken)
      });
      setItems((currentItems) => currentItems.filter((item) => item.id !== itemId));
      setSaveMessage("Voce menu eliminata.");
    } catch (deleteError) {
      const message = deleteError instanceof Error ? deleteError.message : "Errore eliminazione voce menu";
      if (isUnauthorizedAdminError(deleteError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setDeletingItemId("");
    }
  };

  return (
    <section className="admin-section-block">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Menu pizzeria</h3>
        <button
          className="cart-clear-button"
          type="button"
          onClick={() => loadItems({ refreshOnly: true })}
          disabled={isRefreshing || isLoading}
        >
          {isRefreshing ? "Aggiornamento..." : "Ricarica voci"}
        </button>
      </div>

      <p className="admin-row-meta">
        Queste voci sono solo per il menu pizzeria: non sono acquistabili nello shop.
      </p>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {saveMessage && <p className="shop-state admin-success">{saveMessage}</p>}

      <form className="admin-menu-create-form" onSubmit={handleCreateItem}>
        <div className="admin-menu-create-grid admin-pizzeria-create-grid">
          <select
            className="admin-form-input"
            value={newItemDraft.section_key}
            onChange={(event) => handleNewDraftChange("section_key", event.target.value)}
          >
            {SECTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {newItemDraft.section_key === "PIZZE" && (
            <select
              className="admin-form-input"
              value={newItemDraft.subsection_key}
              onChange={(event) => handleNewDraftChange("subsection_key", event.target.value)}
            >
              <option value="PIZZE_CLASSICHE">Pizze classiche</option>
              <option value="PIZZE_BIANCHE">Pizze bianche</option>
            </select>
          )}

          <input
            className="admin-form-input"
            type="text"
            placeholder="Nome voce"
            value={newItemDraft.name}
            onChange={(event) => handleNewDraftChange("name", event.target.value)}
            required
          />
          <input
            className="admin-form-input admin-form-input-wide"
            type="text"
            placeholder="Descrizione / ingredienti (opzionale)"
            value={newItemDraft.description}
            onChange={(event) => handleNewDraftChange("description", event.target.value)}
          />

          <div className="admin-pizzeria-price-grid admin-form-input-wide">
            <input
              className="admin-form-input"
              type="text"
              placeholder="Etichetta prezzo 1 (es. Tonda / Prezzo)"
              value={newItemDraft.price1_label}
              onChange={(event) => handleNewDraftChange("price1_label", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="number"
              min="1"
              step="1"
              placeholder="Importo 1 (centesimi)"
              value={newItemDraft.price1_amount_cents}
              onChange={(event) => handleNewDraftChange("price1_amount_cents", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="text"
              placeholder="Etichetta prezzo 2 (opzionale)"
              value={newItemDraft.price2_label}
              onChange={(event) => handleNewDraftChange("price2_label", event.target.value)}
            />
            <input
              className="admin-form-input"
              type="number"
              min="1"
              step="1"
              placeholder="Importo 2 (opzionale)"
              value={newItemDraft.price2_amount_cents}
              onChange={(event) => handleNewDraftChange("price2_amount_cents", event.target.value)}
            />
          </div>

          <input
            className="admin-form-input"
            type="number"
            min="0"
            step="1"
            placeholder="Ordine"
            value={newItemDraft.sort_order}
            onChange={(event) => handleNewDraftChange("sort_order", event.target.value)}
            required
          />

          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={Boolean(newItemDraft.active)}
              onChange={(event) => handleNewDraftChange("active", event.target.checked)}
            />
            Attiva
          </label>
        </div>

        <button type="submit" className="shop-add-button admin-create-button" disabled={isCreating}>
          {isCreating ? "Creazione..." : "Crea voce menu"}
        </button>
      </form>

      {isLoading && <p className="shop-state">Caricamento voci menu...</p>}
      {!isLoading && items.length === 0 && <p className="shop-state">Nessuna voce menu disponibile.</p>}

      {!isLoading && items.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-menu-table admin-pizzeria-menu-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Struttura</th>
                <th>Voce</th>
                <th>Prezzi</th>
                <th>Ordine / Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const sectionLabel = getSectionLabel(item.section_key);
                const subsectionLabel = getSubsectionLabel(item.subsection_key);
                const isDeleting = deletingItemId === item.id;

                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{shortMenuItemId(item.id)}</strong>
                      <div className="admin-row-meta">{item.id}</div>
                    </td>
                    <td>
                      <div className="admin-row-primary">{sectionLabel}</div>
                      {subsectionLabel && <div className="admin-row-meta">{subsectionLabel}</div>}
                    </td>
                    <td>
                      <div className="admin-row-primary">{item.name}</div>
                      {item.description && <div className="admin-row-meta">{item.description}</div>}
                    </td>
                    <td>
                      <div className="admin-price-lines">
                        {item.prices.map((price, index) => (
                          <div key={`${item.id}-price-${index}`}>
                            {price.label}: <strong>{formatEuro(price.amount_cents)}</strong>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="admin-row-primary">#{item.sort_order}</div>
                      <div className="admin-row-meta">{item.active ? "Attiva" : "Nascosta"}</div>
                    </td>
                    <td>
                      <div className="admin-inline-actions">
                        <Link
                          className="shop-add-button admin-save-button"
                          to={`/admin/menu/${encodeURIComponent(item.id)}/edit`}
                        >
                          Modifica
                        </Link>
                        <button
                          type="button"
                          className="cart-clear-button admin-delete-button"
                          onClick={() => handleItemDelete(item.id)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Eliminazione..." : "Elimina"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminMenuPage;
